import { createContext, ReactNode, useEffect, useState } from 'react';

import { chatAPI } from 'services/api';
import {
  fetchSessionUser,
  isServiceUnavailable,
  isSessionRejected,
  logoutSession,
  startAutoRefresh,
  stopAutoRefresh,
} from 'services/session';
import { connect, reset } from 'services/socket';
import { UsuarioLogadoInterface } from 'store/reducers/usuarios';
import { mapCoreUser } from 'utils/coreAuth';
import removeAuthCookies from 'utils/removeAuthCookies';

const UNAVAILABLE_RETRY_MS = 5 * 1000;
const UNAVAILABLE_RETRY_MAX_MS = 60 * 1000;
const UNAVAILABLE_RETRY_JITTER = 0.2;

// Backoff exponencial (5s, 10s, 20s... até 60s) com jitter para não sincronizar os clientes
function getRetryDelay(attempt: number) {
  const base = Math.min(
    UNAVAILABLE_RETRY_MS * 2 ** attempt,
    UNAVAILABLE_RETRY_MAX_MS,
  );
  const jitter = 1 + (Math.random() * 2 - 1) * UNAVAILABLE_RETRY_JITTER;

  return Math.round(base * jitter);
}

type AuthProviderProps = {
  children: ReactNode;
};

type AlterarSenhaFormValuesType = {
  old_password: string;
  new_password: string;
  new_password_confirmation: string;
};

export declare interface AuthContextInterface {
  loading: boolean;
  failed: boolean;
  isAuthenticated: boolean;
  currentUser: UsuarioLogadoInterface | undefined;
  setCurrentUser: (currentUser: UsuarioLogadoInterface | undefined) => void;
  alterarSenha: (values: AlterarSenhaFormValuesType) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext({} as AuthContextInterface);

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<UsuarioLogadoInterface>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const signOut = async () => {
    setLoading(true);

    try {
      await logoutSession();
    } catch {
      // Falha no logout remoto (ex.: core fora) não impede a saída local
    }
    stopAutoRefresh();
    removeAuthCookies();
    reset();

    setIsAuthenticated(false);
    setCurrentUser(undefined);
    setLoading(false);
  };

  const alterarSenha = (values: AlterarSenhaFormValuesType) => {
    return new Promise<void>((resolve, reject) => {
      chatAPI
        .put('v2/auth/change-password', {
          currentPassword: values.old_password,
          newPassword: values.new_password,
        })
        .then(() => resolve())
        .catch(error => {
          if (error.response) reject(error.response.data.message);
        });
    });
  };

  /**
   * O usuário deve estar autenticado para continuar no app.
   * A sessão vive nos cookies httpOnly do core, então a verificação é feita pelo /me;
   * se for válida, a variável isAuthenticated será `true`.
   */
  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let isMounted = true;
    let attempt = 0;
    let isLoading = false;
    let isLoaded = false;

    const loadSession = () => {
      if (isLoading || isLoaded) return;

      clearTimeout(retryTimer);
      isLoading = true;

      fetchSessionUser()
        .then(me => {
          if (!isMounted) return;

          isLoaded = true;

          setCurrentUser(mapCoreUser(me));
          setIsAuthenticated(true);
          setLoading(false);
          connect();
          startAutoRefresh();
        })
        .catch(error => {
          if (!isMounted) return;

          // Core instável (rede/5xx): não é sessão inválida. Segue no app (o alerta de desconexão
          // do socket avisa o usuário) e tenta carregar o usuário de novo em segundo plano
          if (isServiceUnavailable(error)) {
            setIsAuthenticated(true);
            setLoading(false);
            connect();
            scheduleRetry();

            return;
          }

          isLoaded = true;

          // Só 401/403 (e o refresh recusado) indicam sessão inválida
          if (isSessionRejected(error)) {
            removeAuthCookies();
            setIsAuthenticated(false);
            setLoading(false);

            return;
          }

          // Erro inesperado (ex.: resposta fora do formato): mandar ao login criaria um laço,
          // pois a sessão continua válida e o auth devolve o usuário para cá
          // eslint-disable-next-line no-console
          console.error('Erro ao carregar a sessão:', error);
          setFailed(true);
          setLoading(false);
        })
        .finally(() => {
          isLoading = false;
        });
    };

    // Não gasta requisição com o navegador offline ou a aba oculta: o evento
    // de volta (online/visibilitychange) retoma a tentativa na hora
    function scheduleRetry() {
      clearTimeout(retryTimer);
      if (!navigator.onLine || document.hidden) return;

      retryTimer = setTimeout(loadSession, getRetryDelay(attempt));
      attempt += 1;
    }

    const handleResume = () => {
      if (!navigator.onLine || document.hidden || isLoaded) return;

      attempt = 0;
      loadSession();
    };

    window.addEventListener('online', handleResume);
    document.addEventListener('visibilitychange', handleResume);

    loadSession();

    return () => {
      isMounted = false;
      clearTimeout(retryTimer);
      window.removeEventListener('online', handleResume);
      document.removeEventListener('visibilitychange', handleResume);
      stopAutoRefresh();
    };
  }, []);

  const authContext = {
    currentUser,
    loading,
    failed,
    isAuthenticated,
    signOut,
    alterarSenha,
    setCurrentUser,
  };

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
}
